import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Spinner,
    Image,
    SimpleGrid,
    GridItem,
    Text,
    Flex,
    Button,
    Input,
    InputGroup,
    InputLeftElement,
    InputRightElement,
    ButtonGroup,
    ChakraProvider,
    extendTheme,
    withDefaultColorScheme,
    Popover,
    PopoverTrigger,
    PopoverContent,
    PopoverCloseButton,
    PopoverHeader,
    PopoverBody,
} from '@chakra-ui/react';
import { useHistory, useParams } from 'react-router-dom';
import {
    getCleanItem,
    getUniqueItemsWithSpecialCharFilter,
    imageBaseUrl,
    replaceSpaceWithPlus,
    Send,
} from '../helper';
import { ErrorToast } from '../toast';
import { PromoImage } from '../assets/images/promoImage';
import styled from 'styled-components';
import { AiOutlineMinus, AiOutlinePlus, AiOutlineSearch } from 'react-icons/ai';
import { FaSlidersH } from 'react-icons/fa'
import { TbArrowsSort } from 'react-icons/tb';
import { sortByOptions } from '../constants/shop';
const StyledPromo = styled(PromoImage)`
    position: absolute;
    top: 18px;
    right: -28px;

    @media screen and (max-width: 768px) {
        width: 50px;
        top: -5px;
        right: -13px;
    }
`;
const escapeQuotes = str => {
    // replace iphone keyboard quotes and other quotes
    return str
        ?.toLowerCase()
        ?.replace(/[\u2018\u2019]/g, "'")
        ?.replace(/[\u201C\u201D]/g, '"')
        ?.replace(/['"]+/g, '');
};

const defaultFilters = {
    category: '',
    subcategory: '',
    brand: '',
    retailer: '',
    searchText: '',
    favorites: false,
};

const PublicProfile = () => {
    const history = useHistory();
    const { username } = useParams();
    const inputRef = useRef();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [data, setData] = useState([]);
    const [visibleData, setVisibleData] = useState([]);
    const [dataCopy, setDataCopy] = useState([]);
    const [copyWithoutFilter, setCopyWithoutFilter] = useState([]);

    const [activeFilter, setActiveFilter] = useState('');
    const [searchText, setSearchText] = useState('');
    const [selectedFilterType, setSelectedFilterType] = useState('');
    const [selectedFilter, setSelectedFilter] = useState('');
    const [brandList, setBrandList] = useState([]);
    const [storeList, setStoreList] = useState([]);
    const [categories, setCategories] = useState([]);
    
    const [favoritesOnly, setFavoritesOnly] = useState(false);
    const [retailExpanded, setretailExpanded] = useState(false);
    const [brandExpanded, setBrandExpanded] = useState(false);
    const [filters, setFilters] = useState(defaultFilters);

    const [subCats, setSubCats] = useState([]);
    const [selectedSubCat, setSelectedSubCat] = useState('');
    const [selectedSort, setSelectedSort] = useState('');
    const [hasLoaded, setHasLoaded] = useState(null);

    const theme = extendTheme(
        {
            colors: {
                shop: {
                    400: data.color,
                    500: data.color,
                    600: data.color,
                },
            },
        },
        withDefaultColorScheme({
            colorScheme: 'shop',
        })
    );

    const SearchKeyDown = event => {
        if (event.keyCode === 13) {
            inputRef.current.blur();
        }
    };

    const updateFilterState = (stateKey, value) => {
        if (stateKey === 'category') {
            setActiveFilter(value);
        } else if (stateKey === 'subcategory') {
            setSelectedSubCat(value);
        } else if (stateKey === 'searchText') {
            setSearchText(value);
        } else if (stateKey === 'brand') {
            setSelectedFilter(value)
        } else if (stateKey === 'retailer') {
            setSelectedFilter(value)
        } else if (stateKey === 'favorites') {
            const booleanValue = value?.toLowerCase() === 'true' ? true : false;
            setFavoritesOnly(booleanValue)
        }
    }

    const Init = async () => {
        let res = await Send('GET', '/public/pre-profile/' + username);
        if (res.type === 'error') {
            ErrorToast(res.msg);
            setError(res.msg);
        } else {
            setData(res.data);
            setDataCopy(res.data);
            setCopyWithoutFilter(res.data);
            let cats = ['All'];

            for (let dat of res.data.links) {
                if (!cats.includes(dat.category)) {
                    cats.push(dat.category);
                }
            }
            setCategories(cats);
            InitMore();
        }
    };

    const InitMore = async () => {
        let res = await Send('GET', '/public/profile/' + username);
        if (res.type === 'error') {
            ErrorToast(res.msg);
        } else {
            const newData = res.data;
            setData(newData);
            setDataCopy(newData);
            setCopyWithoutFilter(newData);
            const brandList = [...new Set(newData?.links.map(d => d.brand))];
            const storeList = [...new Set(newData?.links.map(d => d.retailer))];
            setBrandList(brandList);
            setStoreList(storeList);
            let cats = ['All'];

            for (let dat of res.data.links) {
                if (!cats.includes(dat.category)) {
                    cats.push(dat.category);
                }
            }
            setCategories(cats);
            InitDeepLinking(brandList, storeList);
        }
    };

    const InitDeepLinking = async (brands, stores) => {
        const params = new URLSearchParams(window.location.search);
        const newFilters = { ...defaultFilters };
        const filterMappings = {
            activeFilter: 'category',
            selectedSubCat: 'subcategory', 
            selectedBrand: 'brand',
            selectedRetailer: 'retailer',
            searchText: 'searchText',
            favoritesOnly: 'favorites',
        };

        const selectedFilterKey = params.get('selectedFilter');
        if (selectedFilterKey) {
            const filterType = brands.includes(selectedFilterKey) ? 'brand' :
                            stores.includes(selectedFilterKey) ? 'retailer' : null;
            if (filterType) {
                newFilters[filterType] = selectedFilterKey;
                setSelectedFilter(selectedFilterKey);
                setSelectedFilterType(filterType === 'retailer' ? 'store' : filterType) // TODO look into store and retailer discrepancy
            }
        }

        Object.entries(filterMappings).forEach(([paramKey, stateKey]) => {
            let value = params.get(paramKey);

            if (value) {
                newFilters[stateKey] = value;
                updateFilterState(stateKey, value)
            }
        });
        setFilters(newFilters);
        setLoading(false);
        setHasLoaded(true);
    };

        // Filter handlers
    const clearFilterByBrandOrStore = useCallback(() => {
        setSelectedFilter('');
        setSelectedFilterType('');
        setFilters(prev => ({ ...prev, brand: '', retailer: '' }));
        setData(copyWithoutFilter);
    }, [copyWithoutFilter]);

    const filterBy = useCallback((category, ignoreSame) => {
        setSelectedSort('');
        setSelectedSubCat('');
        if ((activeFilter === category || category === 'All') && !ignoreSame) {
            setActiveFilter('All');
            setData(dataCopy);
            setCopyWithoutFilter(dataCopy);
            setSubCats([])
            setFilters(defaultFilters);
            clearFilterByBrandOrStore();
        } else {
            setActiveFilter(category);
            setFilters(prev => ({ ...prev, category, subcategory: '' }));
        }

    },[activeFilter, clearFilterByBrandOrStore, dataCopy]);

    const filterSubCat = useCallback(subcategory => {
        setSelectedSubCat(subcategory);
        setSelectedSort('');
        setFilters(prev => ({ ...prev, subcategory }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[filters]);

    const handleFavorites = useCallback(value => {
        setFilters(prev => ({ ...prev, favorites: value}))
        setFavoritesOnly(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, favoritesOnly]);

    const Search = useCallback((event) => {
        setSearchText(event.target.value);
        setFilters({...filters, searchText: event.target.value});
        const searchArr = event.target.value.split(' ');
    
        let searchData = dataCopy.links.filter(d => {
            let tags = d?.tags;
            tags = tags?.map(tag => escapeQuotes(tag)?.trim().toLowerCase()) || [];
            return (
                searchArr.every(v =>
                    [
                        d.name,
                        d?.brand,
                        d?.retailer,
                        d.subcategory,
                        d.tags.join(' '),
                        d.category
                    ].some(text => escapeQuotes(getCleanItem(text))
                        ?.trim()
                        ?.toLowerCase()
                        .includes(escapeQuotes(getCleanItem(v))))
                ) || tags.includes(escapeQuotes(event.target.value)?.trim().toLowerCase())
            );
        });
    
        let fdata = {
            ...dataCopy,
            links: searchData,
        };

        setData(fdata);
        setCopyWithoutFilter(fdata);
        setVisibleData(fdata.links?.slice(0, 10));
    }, [filters, dataCopy]);



    const applyInitialFilter = useCallback(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const sText = urlParams.get('searchText');
        const aFilter = urlParams.get('activeFilter');
        if (sText) {
            setSearchText(sText);
            Search({
                target: {
                    value: sText,
                },
            });
        }
        if (aFilter) {
            filterBy(aFilter, aFilter === "All" ? false : true);
        }
    
        const sSubCat = urlParams.get('selectedSubCat');
        if (sSubCat) {
            filterSubCat(sSubCat);
        }
    }, [Search, filterBy, filterSubCat]);

    const handleSortBy = e => {
        const sort = e;
        setSelectedSort(sort);
        if (sort === 'bestsellers') {
            let searchData = data.links.sort(
                (a, b) => parseInt(b.clicks) - parseInt(a.clicks)
            );
            let fdata = {
                ...data,
                links: searchData,
            };
            setData(fdata);
            setCopyWithoutFilter(fdata);
            setVisibleData(fdata.links?.slice(0, 10));
        } else if (sort === 'newest') {
            let searchData = data.links.sort(
                (a, b) =>
                    new Date(b.date).getTime() - new Date(a.date).getTime()
            );
            let fdata = {
                ...data,
                links: searchData,
            };
            setData(fdata);
            setCopyWithoutFilter(fdata);
            setVisibleData(fdata.links?.slice(0, 10));
        } else if (sort === 'oldest') {
            let searchData = data.links.sort(
                (a, b) =>
                    new Date(a.date).getTime() - new Date(b.date).getTime()
            );
            let fdata = {
                ...data,
                links: searchData,
            };
            setData(fdata);
            setCopyWithoutFilter(fdata);
            setVisibleData(fdata.links?.slice(0, 10));
        } else if (sort === 'lowtohigh') {
            let searchData = data.links.sort((a, b) => a.price - b.price);
            let fdata = {
                ...data,
                links: searchData,
            };
            setData(fdata);
            setCopyWithoutFilter(fdata);
            setVisibleData(fdata.links?.slice(0, 10));
        } else if (sort === 'hightolow') {
            let searchData = data.links.sort((a, b) => b.price - a.price);
            let fdata = {
                ...data,
                links: searchData,
            };
            setData(fdata);
            setCopyWithoutFilter(fdata);
            setVisibleData(fdata.links?.slice(0, 10));
        }
    };

    const clearSearch = () => {
        setActiveFilter('All');
        setData(dataCopy);
        setCopyWithoutFilter(dataCopy);
        setFilters(defaultFilters);
        clearFilterByBrandOrStore();

        setSearchText('');
    };

    const toDisplay = useMemo(() => {
    return favoritesOnly
        ? (data?.links.filter(f => f.favorite) ?? [])
        : (data?.links ?? []);
}, [data?.links, favoritesOnly]);

    const toggleRetail = () => {
        if (retailExpanded) {
            // setSelectedFilter('');
            // setSelectedFilterType('');
            // setData(dataCopy);
        }
        setSelectedFilterType('store');
        setretailExpanded(!retailExpanded);
    };

    const toggleBrand = () => {
        if (brandExpanded) {
            // setSelectedFilter('');
            // setSelectedFilterType('');
            // setData(dataCopy);
        }
        setSelectedFilterType('brand');
        setBrandExpanded(!brandExpanded);
    };
    const getLink = useCallback((filters) => {
        const newSearchParams = createSearchParams(filters);
        // Update the URL in the browser
        history.push({ search: newSearchParams.toString() });
    }, [history]);

    const createSearchParams = (filters) => {
        const params = new URLSearchParams();

        // Map object keys to the desired URL search parameter names
        if (filters.category) {
            params.set('activeFilter', filters.category);
        }
        if (filters.subcategory) {
            params.set('selectedSubCat', filters.subcategory);
        }

        if (filters.brand) {
            params.set('selectedBrand', filters.brand);
        }
        if (filters.retailer) {
            params.set('selectedRetailer', filters.retailer);
        }
        if (filters.searchText) {
            params.set('searchText', filters.searchText);
        }
        if (filters.favorites) {
            params.set('favoritesOnly', filters.favorites)
        }
        return params;
    }
    const applyFilters = () => {
        let result = dataCopy.links;

    // Check if all main filters are empty
    if (!filters.category && !filters.brand && !filters.retailer && !filters.searchText && !filters.favorites) {
        const allCategories = ['All', ...new Set(result.map(item => item.category))];
        setCategories(allCategories);
        setSubCats([]);
    } else {
        // Apply Category filter
        if (filters.category) {
            result = result.filter(item => getCleanItem(item.category) === getCleanItem(filters.category));
        }

        // Apply Subcategory filter
        if (filters.subcategory) {
            result = result.filter(item => getCleanItem(item.subcategory) === getCleanItem(filters.subcategory));
        }

        // Apply Brand filter
        if (filters.brand) {
            result = result.filter(item => getCleanItem(item.brand) === getCleanItem(filters.brand));
        }

        // Apply Retailer filter
        if (filters.retailer) {
            result = result.filter(item => getCleanItem(item.retailer) === getCleanItem(filters.retailer));
        }

        if (filters.searchText) {
            const searchArr = filters.searchText.split(' ');
            result = result.filter(d => {
                let tags = d?.tags;
                tags = tags?.map(tag => escapeQuotes(tag)?.trim().toLowerCase()) || [];
                return (
                    searchArr.every(v =>
                        [
                            d.name,
                            d?.brand,
                            d?.retailer,
                            d.subcategory,
                            d.tags.join(' '),
                            d.category
                        ].some(text => escapeQuotes(getCleanItem(text))
                            ?.trim()
                            ?.toLowerCase()
                            .includes(escapeQuotes(getCleanItem(v))))
                    ) || tags.includes(escapeQuotes(filters.searchText)?.trim().toLowerCase())
                );
            });
        }

        if (filters.favorites) {
            setFavoritesOnly(filters.favorites);
            result = result.filter(item => item.favorite)
        }

        // Update categories and subcategories based on filtered results
        const filteredCategories = ['All', ...new Set(result.map(item => item.category))];
        const filteredSubCategories = [...new Set(result.map(item => item.subcategory).filter(subcat => subcat !== ''))];

        if (!filters.category && filters.subcategory) {
            setActiveFilter(filteredCategories[1]);
            setFilters({...filters, category: filteredCategories[1]});
        }
        setCategories(filteredCategories);
        setSubCats(filteredSubCategories);
    }

    // Final data manipulation and update visible data
    let fData = {
        ...dataCopy,
        links: result,
    };
    const visibleData = fData.links?.slice(0, 10);
    setData(fData);
    setCopyWithoutFilter(fData);
    setVisibleData(visibleData); // Assuming you want to limit results visible at once
    
    getLink(filters);
    setLoading(false);
    };

    const filterByBrand = brand => {
        setFilters(prev => ({ ...prev, brand }));
    };

    const filterByStore = retailer => {
        setFilters(prev => ({ ...prev, retailer }));
    };

    useEffect(() => {
        Init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (data && data?.links) {
            if (data?.links?.length === 0) {
                setStoreList([]);
                setBrandList([]);
            }
            const brandList = data?.links.map(d => d.brand);
            const storeList = data?.links.map(d => d.retailer);
            setBrandList([...new Set(brandList)]);
            setStoreList([...new Set(storeList)]);
        }
    }, [data]);

    useEffect(() => {
        if (hasLoaded) {
            applyFilters();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, hasLoaded]);

    useEffect(() => {
        if (data?.links?.length > 0 && hasLoaded === null) {
            setHasLoaded(false);
            return;
        }
    }, [applyInitialFilter, data, hasLoaded]);

    useEffect(() => {
        if (!favoritesOnly) {
            // Load initial visible items on component mount
            const initialData = toDisplay?.slice(0, 10);
            setVisibleData(initialData);
        }
        const handleScroll = () => {
            const scrollTop =
                window?.scrollY || document.documentElement.scrollTop;
            const clientHeight = document.documentElement.clientHeight;
            /* const startIndex = Math.floor(scrollTop / 50); */
            const endIndex = Math.min(
                Math.ceil((scrollTop + clientHeight) / 50) - 1,
                toDisplay.length - 1
            );
            const newData = toDisplay?.slice(0, endIndex + 1); // Updated range to show first endIndex + 1 items
            setVisibleData(newData);
        };
        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [favoritesOnly, toDisplay]);

    if (error) {
        return (
            <>
                <Box width={'100vw'} background="#FAFAFA" padding="16px 8px">
                    {error}
                </Box>
            </>
        );
    }
    return (
        <>
            <link
                href={`https://fonts.googleapis.com/css2?family=${replaceSpaceWithPlus(
                    data?.shopFont
                )}&display=swap:wght@100;300;400;500;700;800;900`}
                rel="stylesheet"
            />
            <Box
                fontFamily={`"${data?.shopFont || 'Roboto'}"`}
                color={data?.fontColor}
            >
                <>
                    {/* <Center> */}
                    <Box
                        width={'100vw'}
                        background="#FFF"
                        padding="16px 8px"
                    >
                        <Image
                            width={'90%'}
                            ml={'5%'}
                            mr={'5%'}
                            height="100%"
                            maxHeight={'20vh'}
                            objectFit={'contain'}
                            src={imageBaseUrl + data.shopImage}
                            alt="Shop Image"
                        />
                    </Box>
                    <Text color={data?.fontColor} textAlign={'center'} mt={0}>
                        Disclosure: may contain commissionable links.
                    </Text>
                    <SimpleGrid
                        columns={1}
                        justifyContent={'center'}
                        gridTemplateColumns={{ base: 'auto', md: '60ch' }}
                        gap="0px 0"
                        padding={{ base: '20px 20px 20px 10px', md: '10px 0' }}
                    >
                        <GridItem>
                            <Box display="flex" flexDirection="row" alignItems="center" width="100%">
                                <InputGroup
                                    transition="border-color 150ms ease"
                                    _focusWithin={{ borderColor: 'gray.500' }}
                                    width="auto"
                                >
                                <InputLeftElement
                                    height="48px"
                                    children={
                                        <Box
                                            bgColor={'transparent'}
                                            borderRadius="50%"
                                            fontSize={"3xl"}
                                        >
                                            <AiOutlineSearch style={{ fontSize: '32px', padding: '4px' }} color={data?.fontColor} width={22} height={22}/>
                                        </Box>
                                    }
                                />
                                <Input
                                    size={'lg'}
                                    onChange={Search}
                                    onKeyDown={SearchKeyDown}
                                    value={searchText}
                                    ref={inputRef}
                                    placeholder="Search"
                                    _placeholder={{ color: data?.fontColor ? data.fontColor : '#333f4c', fontFamily:data?.shopFont ? data.shopFont : 'Cormorant Garamond', lineHeight:'27px', fontSize:'18px', opacity: 0.75 }}
                                    border="none"
                                    _focus={{ outline: '0' }}
                                />
                                <InputRightElement
                                    height="48px"
                                    children={
                                    <Box
                                        cursor={'pointer'}
                                        onClick={clearSearch}
                                        display="flex"
                                        alignItems="center"
                                    >
                                        <svg
                                        width="22"
                                        height="22"
                                        style={{
                                            width: '18px',
                                        }}
                                        viewBox="0 0 22 22"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                        >
                                        <path
                                            d="M1.4 1L21 21M20.6 1L1 21"
                                            stroke={data.fontColor}
                                            strokeWidth="2"
                                        />
                                        </svg>
                                    </Box>
                                    }
                                />
                                </InputGroup>
                                {/* Popover for Sort */}
                                <Popover
                                    placement="bottom-start"
                                    closeOnBlur={true}
                                >
                                    {({ isOpen, onClose }) => (
                                        <>
                                            <PopoverTrigger>
                                                <Box
                                                    value={selectedSort}
                                                    onChange={handleSortBy}
                                                    color={
                                                        selectedSort
                                                            ? data?.fontColor
                                                            : data?.fontColor
                                                    }
                                                    borderRadius={0}
                                                    borderColor="#D0D4E3"
                                                    border={0}
                                                    height="48px"
                                                    iconSize="36px"
                                                    fontSize="18px"
                                                    borderWidth="1px"
                                                    paddingX={3}
                                                    justifyContent={'space-between'}
                                                    alignItems={'center'}
                                                    display={'flex'}
                                                    cursor="pointer"
                                                    _focus={{ outline: 'none' }}
                                                    outline="none"
                                                >
                                                    <Box
                                                        display="flex"
                                                        justifyContent="center"
                                                        alignItems="center"
                                                        backgroundColor="#F0F0F0"
                                                        borderRadius="50%"
                                                        width="36px"
                                                        height="36px"
                                                    >
                                                        <TbArrowsSort color='#616a6b' fontSize={16}/>
                                                    </Box>
                                                </Box>
                                            </PopoverTrigger>
                                            {isOpen && (
                                                <PopoverContent
                                                    outline={'none'}
                                                    _focus={{
                                                        outline: 'none',
                                                        borderColor:
                                                            data?.fontColor,
                                                    }}
                                                    boxSize={'xxs'}
                                                >
                                                    <PopoverCloseButton
                                                        onClick={() => {
                                                            // set default sort to  newest
                                                            handleSortBy('newest');
                                                            onClose();
                                                        }}
                                                    />
                                                    <PopoverHeader
                                                        borderColor={
                                                            data?.fontColor
                                                        }
                                                    >
                                                        Sort By
                                                    </PopoverHeader>
                                                    <PopoverBody outline="none">
                                                        <Box
                                                            cursor={'pointer'}
                                                            mb={2}
                                                        >
                                                            {sortByOptions?.map(
                                                                d => {
                                                                    return (
                                                                        <Text
                                                                            key={
                                                                                d.value
                                                                            }
                                                                            mb={3}
                                                                            cursor={
                                                                                'pointer'
                                                                            }
                                                                            fontWeight={
                                                                                selectedSort ===
                                                                                d.value
                                                                                    ? 'bold'
                                                                                    : 'normal'
                                                                            }
                                                                            p={1}
                                                                            onClick={() => {
                                                                                handleSortBy(
                                                                                    d.value
                                                                                );
                                                                                onClose();
                                                                            }}
                                                                            fontSize={
                                                                                '18px'
                                                                            }
                                                                        >
                                                                            {
                                                                                d.label
                                                                            }
                                                                        </Text>
                                                                    );
                                                                }
                                                            )}
                                                        </Box>
                                                    </PopoverBody>
                                                </PopoverContent>
                                            )}
                                        </>
                                    )}
                                </Popover>
                                {/*Popover for Filter */}
                                <Popover
                                    placement="bottom-start"
                                    closeOnBlur={true}
                                 >
                                {({ isOpen, onClose }) => (
                                    <>
                                        <PopoverTrigger>
                                            <Box
                                                onChange={handleSortBy}
                                                color={
                                                    selectedFilter
                                                        ? data?.fontColor
                                                        : data?.fontColor
                                                }
                                                borderRadius={0}
                                                borderColor="#D0D4E3"
                                                border={0}
                                                height="48px"
                                                iconSize="36px"
                                                fontSize="18px"
                                                borderWidth="1px"
                                                justifyContent={'space-between'}
                                                alignItems={'center'}
                                                display={'flex'}
                                                cursor="pointer"
                                                _focus={{ outline: 'none' }}
                                                outline="none"
                                            >
                                                <Box
                                                    display="flex"
                                                    justifyContent="center"
                                                    alignItems="center"
                                                    backgroundColor="#F0F0F0"
                                                    borderRadius="50%"
                                                    width="36px"
                                                    height="36px"
                                                >
                                                <FaSlidersH color='#616a6b' fontSize={16}/>
                                                </Box>
                                            </Box>
                                        </PopoverTrigger>
                                        {isOpen && (
                                            <PopoverContent
                                                outline={'none'}
                                                _focus={{
                                                    outline: 'none',
                                                    borderColor:
                                                        data?.fontColor,
                                                }}
                                            >
                                                <PopoverCloseButton
                                                    onClick={() => {
                                                        onClose();
                                                        clearFilterByBrandOrStore();
                                                    }}
                                                />
                                                <PopoverHeader
                                                    borderColor={
                                                        data?.fontColor
                                                    }
                                                >
                                                    <SimpleGrid
                                                        columns={1}
                                                        justifyContent={
                                                            'center'
                                                        }
                                                        gridTemplateColumns={{
                                                            base: '1fr',
                                                            md: '60ch',
                                                        }}
                                                        gap="8px 0"
                                                        padding={{
                                                            base: '12px 10px',
                                                            md: '2px 0',
                                                        }}
                                                    >
                                                        <GridItem
                                                            display={'flex'}
                                                            justifyContent={
                                                                'center'
                                                            }
                                                            ml={'-25px'}
                                                            mt={2}
                                                        >
                                                            Filter By
                                                            <ChakraProvider
                                                                theme={theme}
                                                            >
                                                                {toDisplay?.filter(
                                                                    d =>
                                                                        d.favorite
                                                                )?.length >
                                                                    0 && (
                                                                    <ButtonGroup
                                                                        size={
                                                                            'sm'
                                                                        }
                                                                        ml={
                                                                            '40px'
                                                                        }
                                                                        isAttached
                                                                    >
                                                                        <Button
                                                                            onClick={() =>
                                                                                handleFavorites(false)
                                                                            }
                                                                            variant={
                                                                                favoritesOnly
                                                                                    ? 'outline'
                                                                                    : 'solid'
                                                                            }
                                                                        >
                                                                            All
                                                                        </Button>
                                                                        <Button
                                                                            onClick={() => {
                                                                                handleFavorites(true)
                                                                                setVisibleData(
                                                                                    toDisplay
                                                                                        ?.filter(
                                                                                            d =>
                                                                                                d.favorite
                                                                                        )
                                                                                        ?.slice(
                                                                                            0,
                                                                                            10
                                                                                        )
                                                                                );
                                                                            }}
                                                                            variant={
                                                                                !favoritesOnly
                                                                                    ? 'outline'
                                                                                    : 'solid'
                                                                            }
                                                                        >
                                                                            Favorites
                                                                        </Button>
                                                                    </ButtonGroup>
                                                                )}
                                                            </ChakraProvider>
                                                        </GridItem>
                                                    </SimpleGrid>
                                                </PopoverHeader>
                                                <PopoverBody outline="none">
                                                    <Box
                                                        cursor={'pointer'}
                                                        onClick={toggleBrand}
                                                        display="flex"
                                                        justifyContent={
                                                            'space-between'
                                                        }
                                                        mb={2}
                                                    >
                                                        <Text fontSize={'18px'}>
                                                            Brand
                                                        </Text>
                                                        {brandExpanded ? (
                                                            <AiOutlineMinus
                                                                color={
                                                                    data?.fontColor
                                                                }
                                                            />
                                                        ) : (
                                                            <AiOutlinePlus
                                                                color={
                                                                    data?.fontColor
                                                                }
                                                            />
                                                        )}
                                                    </Box>
                                                    {brandExpanded && (
                                                        <>
                                                            {getUniqueItemsWithSpecialCharFilter(
                                                                brandList
                                                            )
                                                                ?.filter(
                                                                    d =>
                                                                        d?.length >
                                                                        0
                                                                )
                                                                // sort alphabetically and map
                                                                ?.sort((a, b) =>
                                                                    a.localeCompare(
                                                                        b
                                                                    )
                                                                )

                                                                ?.map(d => {
                                                                    return (
                                                                        <Text
                                                                            key={
                                                                                d
                                                                            }
                                                                            mb={
                                                                                3
                                                                            }
                                                                            ml={
                                                                                1
                                                                            }
                                                                            cursor={
                                                                                'pointer'
                                                                            }
                                                                            fontWeight={
                                                                                selectedFilterType ===
                                                                                    'brand' &&
                                                                                selectedFilter ===
                                                                                    d
                                                                                    ? 'bold'
                                                                                    : 'normal'
                                                                            }
                                                                            p={
                                                                                1
                                                                            }
                                                                            onClick={() => {
                                                                                setSelectedFilter(
                                                                                    d
                                                                                );
                                                                                filterByBrand(
                                                                                    d
                                                                                );
                                                                                onClose();
                                                                            }}
                                                                            fontSize={
                                                                                '18px'
                                                                            }
                                                                        >
                                                                            {d}
                                                                        </Text>
                                                                    );
                                                                })}
                                                        </>
                                                    )}

                                                    <Box
                                                        cursor={'pointer'}
                                                        onClick={toggleRetail}
                                                        display="flex"
                                                        justifyContent={
                                                            'space-between'
                                                        }
                                                        mb={2}
                                                    >
                                                        <Text fontSize={'18px'}>
                                                            Retailer
                                                        </Text>
                                                        {retailExpanded ? (
                                                            <AiOutlineMinus
                                                                color={
                                                                    data?.fontColor
                                                                }
                                                            />
                                                        ) : (
                                                            <AiOutlinePlus
                                                                color={
                                                                    data?.fontColor
                                                                }
                                                            />
                                                        )}
                                                    </Box>
                                                    {retailExpanded && (
                                                        <>
                                                            {getUniqueItemsWithSpecialCharFilter(
                                                                storeList
                                                            )
                                                                ?.filter(
                                                                    d =>
                                                                        d?.length >
                                                                        0
                                                                )
                                                                ?.sort((a, b) =>
                                                                    a.localeCompare(
                                                                        b
                                                                    )
                                                                )
                                                                ?.map(d => {
                                                                    return (
                                                                        <Text
                                                                            key={
                                                                                d
                                                                            }
                                                                            mb={
                                                                                3
                                                                            }
                                                                            ml={
                                                                                1
                                                                            }
                                                                            cursor={
                                                                                'pointer'
                                                                            }
                                                                            fontWeight={
                                                                                selectedFilterType ===
                                                                                    'store' &&
                                                                                selectedFilter ===
                                                                                    d
                                                                                    ? 'bold'
                                                                                    : 'normal'
                                                                            }
                                                                            p={
                                                                                1
                                                                            }
                                                                            onClick={() => {
                                                                                setSelectedFilter(
                                                                                    d
                                                                                );
                                                                                filterByStore(
                                                                                    d
                                                                                );
                                                                                onClose();
                                                                            }}
                                                                            fontSize={
                                                                                '18px'
                                                                            }
                                                                        >
                                                                            {d}
                                                                        </Text>
                                                                    );
                                                                })}
                                                        </>
                                                    )}
                                                </PopoverBody>
                                            </PopoverContent>
                                        )}
                                    </>
                                )}
                                </Popover>
                            </Box>
                            </GridItem>

                        <GridItem>
                            <Popover
                                placement="bottom-start"
                                closeOnBlur={true}
                            >
                                {({ isOpen, onClose }) => (
                                    <>
                                        <PopoverTrigger>
                                            <Box
                                                value={activeFilter}
                                                color={
                                                    activeFilter
                                                        ? data?.fontColor
                                                        : data?.fontColor
                                                }
                                                borderRadius={0}
                                                borderColor="#D0D4E3"
                                                border={0}
                                                height="48px"
                                                iconSize="36px"
                                                fontSize="18px"
                                                borderWidth="1px"
                                                justifyContent={'flex-start'}
                                                alignItems={'center'}
                                                display={'flex'}
                                                cursor="pointer"
                                                padding={'0 12px'}
                                                _focus={{ outline: 'none' }}
                                                outline="none"
                                            >
                                                <Text>
                                                    {activeFilter === ''
                                                        ? 'Category'
                                                        : activeFilter}
                                                </Text>
                                            </Box>
                                        </PopoverTrigger>
                                        {isOpen && (
                                            <PopoverContent
                                                outline={'none'}
                                                _focus={{
                                                    outline: 'none',
                                                    borderColor:
                                                        data?.fontColor,
                                                }}
                                                maxWidth={'200px'}
                                            >
                                                <PopoverCloseButton
                                                    onClick={() => {
                                                        filterBy(activeFilter);
                                                        onClose();
                                                    }}
                                                />
                                                <PopoverHeader
                                                    borderColor={
                                                        data?.fontColor
                                                    }
                                                >
                                                    Select Category
                                                </PopoverHeader>
                                                <PopoverBody outline="none">
                                                    <Box
                                                        cursor={'pointer'}
                                                        mb={2}
                                                    >
                                                        {/* sort alphabetically and map */}
                                                        {getUniqueItemsWithSpecialCharFilter(
                                                            categories
                                                        )
                                                            ?.sort((a, b) =>
                                                                a.localeCompare(
                                                                    b
                                                                )
                                                            )
                                                            ?.map((d, i) => {
                                                                return (
                                                                    <Text
                                                                        key={i}
                                                                        mb={3}
                                                                        cursor={
                                                                            'pointer'
                                                                        }
                                                                        fontWeight={
                                                                            activeFilter ===
                                                                            d
                                                                                ? 'bold'
                                                                                : 'normal'
                                                                        }
                                                                        p={1}
                                                                        onClick={() => {
                                                                            filterBy(
                                                                                d
                                                                            );
                                                                            onClose();
                                                                        }}
                                                                        fontSize={
                                                                            '18px'
                                                                        }
                                                                    >
                                                                        {d}
                                                                    </Text>
                                                                );
                                                            })}
                                                    </Box>
                                                </PopoverBody>
                                            </PopoverContent>
                                        )}
                                    </>
                                )}
                            </Popover>
                        </GridItem>
                        <GridItem>
                            <Popover
                                placement="bottom-start"
                                closeOnBlur={true}
                            >
                                {({ isOpen, onClose }) => (
                                    <>
                                        {subCats.length > 0 && (
                                            <>
                                                <PopoverTrigger>
                                                    <Box
                                                        value={selectedSubCat}
                                                        color={
                                                            selectedSubCat
                                                                ? data?.fontColor
                                                                : data?.fontColor
                                                        }
                                                        borderRadius={0}
                                                        borderColor="#D0D4E3"
                                                        border={0}
                                                        height="48px"
                                                        iconSize="36px"
                                                        fontSize="18px"
                                                        borderWidth="1px"
                                                        justifyContent={
                                                            'space-between'
                                                        }
                                                        alignItems={'center'}
                                                        display={'flex'}
                                                        cursor="pointer"
                                                        padding={'0 15px'}
                                                        _focus={{
                                                            outline: 'none',
                                                        }}
                                                        outline="none"
                                                    >
                                                        <Text>
                                                            {selectedSubCat ===
                                                            ''
                                                                ? 'Subcategory'
                                                                : selectedSubCat}
                                                        </Text>
                                                    </Box>
                                                </PopoverTrigger>
                                                {isOpen && (
                                                    <PopoverContent
                                                        outline={'none'}
                                                        maxWidth={'200px'}
                                                        _focus={{
                                                            outline: 'none',
                                                            borderColor:
                                                                data?.fontColor,
                                                        }}
                                                    >
                                                        <PopoverCloseButton
                                                            onClick={() => {
                                                                filterSubCat(
                                                                    ''
                                                                );

                                                                onClose();
                                                            }}
                                                        />
                                                        <PopoverHeader
                                                            borderColor={
                                                                data?.fontColor
                                                            }
                                                        >
                                                            Select Subcategory
                                                        </PopoverHeader>
                                                        <PopoverBody outline="none">
                                                            <Box
                                                                cursor={
                                                                    'pointer'
                                                                }
                                                                mb={2}
                                                            >
                                                                {/* sort alphabetically and map */}

                                                                {getUniqueItemsWithSpecialCharFilter(
                                                                    subCats
                                                                )
                                                                    ?.sort(
                                                                        (
                                                                            a,
                                                                            b
                                                                        ) =>
                                                                            a.localeCompare(
                                                                                b
                                                                            )
                                                                    )
                                                                    .map(
                                                                        (
                                                                            d,
                                                                            i
                                                                        ) => {
                                                                            return (
                                                                                <Text
                                                                                    key={
                                                                                        i
                                                                                    }
                                                                                    mb={
                                                                                        3
                                                                                    }
                                                                                    cursor={
                                                                                        'pointer'
                                                                                    }
                                                                                    fontWeight={
                                                                                        selectedSubCat ===
                                                                                        d
                                                                                            ? 'bold'
                                                                                            : 'normal'
                                                                                    }
                                                                                    p={
                                                                                        1
                                                                                    }
                                                                                    onClick={() => {
                                                                                        filterSubCat(
                                                                                            d
                                                                                        );
                                                                                        onClose();
                                                                                    }}
                                                                                    fontSize={
                                                                                        '18px'
                                                                                    }
                                                                                >
                                                                                    {
                                                                                        d
                                                                                    }
                                                                                </Text>
                                                                            );
                                                                        }
                                                                    )}
                                                            </Box>
                                                        </PopoverBody>
                                                    </PopoverContent>
                                                )}
                                            </>
                                        )}
                                    </>
                                )}
                            </Popover>
                        </GridItem>
                    </SimpleGrid>

                    {loading && !hasLoaded && (
                        <Flex justifyContent={'center'}>
                            <Box alignItems="center" justifyItems="center">
                                <Spinner />
                            </Box>
                        </Flex>
                    )} 
                    {!loading && hasLoaded && (<Flex justifyContent={'center'}>
                    {visibleData.length === 0 && (
                                <Box alignItems="center" justifyItems="center">

                                    <Text alignItems="center" justifyItems="center" >No results found. Please refine your search and try again.</Text>
                                </Box>
                            )}
                        <SimpleGrid
                            columns={{ sm: 2, md: 3 }}
                            m={{ base: 0, md: 10 }}
                            columnGap={{ md: '28', base: 10 }}
                            rowGap={10}
                            justifyItems="center"
                            alignItems="flex-start"
                        >
                            {visibleData?.map((d, i) => {
                                return (
                                    <GridItem
                                        key={i}
                                        width={{
                                            base: '150px',
                                            md: '280px',
                                        }}
                                    >
                                        <Box
                                            position="relative"
                                            width={{
                                                base: '150px',
                                                md: '280px',
                                            }}
                                            height={{
                                                base: '150px',
                                                md: '280px',
                                            }}
                                            // backgroundImage={`url("${imageBaseUrl}${d.image}")`}
                                            backgroundSize="contain"
                                            backgroundRepeat="no-repeat"
                                            backgroundPosition="center"
                                            display="flex"
                                            mb="16px"
                                            cursor="pointer"
                                            justifyContent={'center'}
                                            as="a"
                                            href={'/goto/' + d.unique_id}
                                            target="_blank"
                                            // onClick={() =>
                                            //     goToLink(d.unique_id)
                                            // }
                                        >
                                            <img
                                                src={`${imageBaseUrl}${d.image}`}
                                                alt="link"
                                                loading="lazy"
                                                style={{
                                                    objectFit: 'contain',
                                                }}
                                            />

                                            {d.coupon && (
                                                <StyledPromo
                                                    color={data.color}
                                                    fontFamily={data.shopFont}
                                                />
                                            )}
                                        </Box>
                                        <Text
                                            align="center"
                                            color={data?.fontColor}
                                            mb="4px"
                                            fontSize={{
                                                base: '14px',
                                                md: '18px',
                                            }}
                                            fontWeight="700"
                                        >
                                            {d.name}
                                        </Text>
                                        {d.price && d.price > 0 ? (
                                            <Text
                                                align="center"
                                                color={data?.fontColor}
                                                fontSize={{
                                                    base: '12px',
                                                    md: '14px',
                                                }}
                                            >
                                                ${d.price}
                                            </Text>
                                        ) : null}
                                        {d.coupon ? (
                                            <Text
                                                align="center"
                                                color={data?.fontColor}
                                                fontSize={{
                                                    base: '12px',
                                                    md: '14px',
                                                }}
                                            >
                                                <Text
                                                    display="inline"
                                                    userSelect="none"
                                                >
                                                    Code:{' '}
                                                </Text>
                                                {d.coupon}
                                            </Text>
                                        ) : (
                                            <Box height="21px" />
                                        )}
                                    </GridItem>
                                );
                            })}
                        </SimpleGrid>
                    </Flex>)}
                </>
            </Box>
        </>
    );
};
export default PublicProfile;
